import { ContactModel } from "../ContactModel";
import {
    DetailItem,
    DetailSection,
    AppEnumHelper,
    ContactEnums,
    stringCompare,
    NeedEnums,
    MatchingEnums,
    DateFormatHelper,
    OptionHelper,
    DOMAIN_TYPES,
    formatName,
    SoNumber,
    RecordMap
} from "@/Shared";

import { PersonViewModel } from "@/Domains/Person/Logic/ViewModels/PersonViewModel";
import { AccessRightModel } from "@/Domains/Auth";
import { Row } from "@/Shared/UI/Components/DetailsListBox/DetailsListBox";
import { DocumentModel } from "@/Domains/Document";


export enum CONTACT_TYPES {
    OWNER = 'owner',
    TENANT = 'tenant',
    BUYER = 'buyer',
    OWNER_PROSPECT = 'owner_prospect',
    TENANT_PROSPECT = 'tenant_prospect',
    BUYER_PROSPECT = 'buyer_prospect'
}


export interface ContactVMParams {
    contact: ContactModel;
    viewEnums: ContactEnums;
    needEnums: NeedEnums;
    matchingEnums: MatchingEnums;
    accessRight: AccessRightModel;
    isDisplaying: boolean;
    documentMap?: RecordMap<DocumentModel>;
}

/**
 * Class: ContactViewModel
 *
 * Description:
 * ViewModel for displaying contact information in the UI.
 * Extends PersonViewModel to inherit personal info.
 * Handles sections for civilité, other info, history, needs, and matching.
 * Provides utility methods for checking closed status and owner options.
 *
 * Author: Samano CASTRE
 * Date: July 17, 2025
 */
export class ContactViewModel extends PersonViewModel {

    public closed!: boolean;
    public contactType!: string;
    public agent!: string;
    public note!: string;
    public origin!: string;
    public model!: ContactModel;
    public isDeleted!: boolean;
    public cardClass!: string;
    private _documentMap?: RecordMap<DocumentModel>;

    /**
     * =====================================================
     * Constructor
     * =====================================================
     *
     * Initializes a contact view model from a ContactModel and related enums
     *
     * @param params - Parameters containing contact, enums, access rights, display flags
     */
    public constructor(private params: ContactVMParams) {
        super(params.contact.person, params.viewEnums.CivilityEnum);

        const { contact, viewEnums, isDisplaying, documentMap } = this.params;

        /**
         * =====================================================
         * Initialization
         * =====================================================
         *
         * Sets all display properties, audit, card class, and formatting.
         */
        this.audit.initialize(contact);
        this.contactType = AppEnumHelper.getLabel(viewEnums.ContactTypeEnum, contact.contactType);
        this.note = contact?.note || "";
        this.origin = contact?.origin || "";
        this.agent = formatName(contact?.agent?.person);
        this.closed = !!contact.closedAt;
        this.model = contact;
        this.isDeleted = !!contact.deletedAt
        this.cardClass = isDisplaying ? 'active' : '';
        this._documentMap = documentMap;
    }

    hasDocument(): boolean {

        return this._documentMap ? !this._documentMap.isEmpty() : false;
    }

    documentMap() : RecordMap<DocumentModel>|undefined {
        return this._documentMap;
    }

    public isOwnerGroup = () => {
        return AppEnumHelper.getByValue(
            this.params.viewEnums.ContactTypeEnum,
            this.model.contactType
        )?.group === 'owner';
    }

    /**
     * =====================================================
     * Status Utilities
     * =====================================================
     */
    isClosed = () => this.closed;

    get civilitySection() {
        return [
            { label: "Nom", value: this.lastname },
            { label: "Prénom", value: this.firstname },
            { label: "Civilité", value: this.civility },
            { label: "Type", value: this.contactType },
            { label: "Profession", value: this.profession },
            { label: "Date de Naissance", value: this.birthDate }

        ] as DetailItem[];
    }

    /**
     * =====================================================
     * Sections for UI Display
     * =====================================================
     */
    get otherSection() {
        return [
            { label: "Note", value: this.note },
            { label: "Origine", value: this.origin },
            { label: "Suivi par", value: this.agent },
            { label: "Date création", value: this.creationDate }
        ] as DetailItem[];
    }

    get historySection() {
        const headers = ["Date", "Type", "Conseillé", "Note"];
        const rows = [
            {
                items: [
                    { label: this.creationDate },
                    { label: this.contactType },
                    { label: this.agent },
                    { label: this.note }
                ]
            }
        ] as Row[];
        return { headers, rows };
    }

    get needsSection() {
        const headers = ["Date", "Type", "Chambres", "Résultats"];
        const rows = (this.params.contact.needList ?? []).map(need => {
            return {
                items: [
                    { label: DateFormatHelper.toLocaleDateString(need.createdAt) },
                    { label: AppEnumHelper.getLabel(this.params.needEnums.OperationTypeEnum, need.operationType) },
                    { label:  AppEnumHelper.getLabel(this.params.needEnums.PropertyTypeEnum, need.propertyType) },
                    { label: AppEnumHelper.getLabel(this.params.needEnums.NeedStatusEnum, need.status) }
                ]
            }
        });

        return { headers, rows };
    }

    get matchSection() {
        const headers = ["N° Bien", "Date disponible", "Type de bien", "Prix"];

        const rows = (this.params.contact.matchingList ?? []).map(matching => {
            return {
                items: [
                    { label: String(matching.propertyId) },
                    { label: DateFormatHelper.toLocaleDateString(matching.property?.availableAt) },
                    { label:  AppEnumHelper.getLabel(this.params.needEnums.PropertyTypeEnum, matching.property?.propertyType) },
                    { label: SoNumber.formatAmount(matching.property?.price)}
                ]
            }
        }
        );
        return { headers, rows };
    }

    /**
     * =====================================================
     * Sections Aggregator
     * =====================================================
     *
     * Returns all detail sections including audit trail if permitted
     */
    getAllSections = (): DetailSection[] => {
        const sections: DetailSection[] = [
            {
                sectionFn: () => this.civilitySection,
                legend: 'Civilité',
                type: 'box'
            },
            {
                sectionFn: () => this.localisationSection,
                legend: 'Localisation',
                type: 'box'
            },
            {
                sectionFn: () => this.coordinatesSection,
                legend: 'Coordonnées',
                type: 'box'
            },
            {
                sectionFn: () => this.otherSection,
                legend: 'Autres',
                type: 'box'
            },
            {
                sectionFn: () => this.historySection,
                legend: 'Historique',
                type: 'list'
            },
            {
                sectionFn: () => this.needsSection,
                legend: 'Mes besoins',
                type: 'list'
            },
            {
                sectionFn: () => this.matchSection,
                legend: 'Rapprochements',
                type: 'list'
            }
        ];

        if (this.params.accessRight.canAudit(DOMAIN_TYPES.Contact)) {
            sections.push(
                {
                    sectionFn: this.audit.getAuditTrail,
                    legend: 'Traçabilité',
                    type: 'box'
                }
            );
        }
        return sections;
    }

    get title() {
        return super.getTitle("Contact");
    }

    /**
     * =====================================================
     * Comparison
     * =====================================================
     *
     * Allows sorting by full name
     */
    compareTo(other: ContactViewModel): number {
        return stringCompare(this.fullName, other.fullName)
    }
}